import { useEffect, useState } from 'react';
import { usePromisify } from '../services/asyncController/usePromisify';
import { usePaymentControllerData } from '../components/PaymentPage/hooks/UsePaymentControllerData';
import { OrderPrice } from '../services/getOrderPrice';

export const useOrderPrice = (
  orderId: string | null,
  enabled: boolean,
  refreshKey?: number,
): OrderPrice | null => {
  const { getOrder: getOrderRaw } = usePaymentControllerData();
  const getOrder = usePromisify(getOrderRaw);
  const [orderPrice, setOrderPrice] = useState<OrderPrice | null>(null);

  useEffect(() => {
    if (!enabled || !orderId) {
      return;
    }

    async function fetchOrderPrice() {
      try {
        const response = await getOrder({ orderId });
        const order = response?.order;

        if (order?.description) {
          const finalAmount = order.description.amount?.toString() || '0';
          const currency = order.description.currency || 'USD';
          const discountAmount = order.description.charges?.discount || 0;
          const hasCoupon = discountAmount > 0;
          const couponId = order.description.externalData?.couponId;

          // Calculate original amount if there's a discount
          let originalAmount: string | undefined;
          if (hasCoupon && order.description.items?.[0]?.price) {
            originalAmount = order.description.items[0].price.toString();
          }

          setOrderPrice({
            amount: finalAmount,
            currency,
            originalAmount,
            hasCoupon,
            discount:
              discountAmount > 0 ? discountAmount.toString() : undefined,
            couponId,
          });
        } else {
          setOrderPrice(null);
        }
      } catch (error) {
        console.error('Error fetching order price:', error);
        setOrderPrice(null);
      }
    }

    fetchOrderPrice();
  }, [orderId, enabled, getOrder, refreshKey]);

  return orderPrice;
};
